import type { IRbac, IRbacPermission } from "../apis/rbac/rbac.interfaces";

/**
 * Converts camelCase to Title Case (e.g. spocPremise → "Spoc Premise")
 */
export function camelCaseToTitleCase(str: string): string {
  if (!str) return "";
  return str
    .replace(/([A-Z])/g, " $1")
    .replace(/^./, (s) => s.toUpperCase())
    .trim();
}

/**
 * Deep clone IRbac with all read/write/delete set to false
 */
export function createEmptyPermissions(structure: IRbac): IRbac {
  const result: IRbac = {};
  for (const [module, submodules] of Object.entries(structure)) {
    result[module] = {};
    for (const submodule of Object.keys(submodules)) {
      result[module][submodule] = {
        read: false,
        write: false,
        delete: false,
      };
    }
  }
  return result;
}

/**
 * Deep clone IRbac for edit mode
 */
export function clonePermissions(permissions: IRbac): IRbac {
  const result: IRbac = {};
  for (const [module, submodules] of Object.entries(permissions)) {
    result[module] = {};
    for (const [submodule, perm] of Object.entries(submodules)) {
      result[module][submodule] = { ...perm };
    }
  }
  return result;
}

/**
 * Returns an IRbac whose module/submodule shape matches `structure`,
 * with values taken from `permissions` when present and from `structure` otherwise.
 */
/**
 * Folds legacy top-level `academic` module (pre–unified Academics nav) into `academics`.
 * Maps `gradeLevels` → `scoringRubrics`. Idempotent if `academic` is absent.
 */
export function normalizeLegacyRbacPermissions(permissions: IRbac): IRbac {
  const legacy = permissions.academic;
  if (!legacy) return permissions;

  const next = clonePermissions(permissions);
  delete next.academic;

  if (!next.academics) next.academics = {};

  const fold = (sub: string, perm: IRbacPermission | undefined) => {
    if (!perm) return;
    const cur = next.academics![sub];
    next.academics![sub] = cur ? { ...cur, ...perm } : { ...perm };
  };

  fold("courses", legacy.courses);
  fold("classes", legacy.classes);
  fold("syllabus", legacy.syllabus);
  fold("syllabusAssignment", legacy.syllabusAssignment);
  fold("scoringRubrics", legacy.gradeLevels);

  return next;
}

export function mergePermissionsWithStructure(
  permissions: IRbac | undefined,
  structure: IRbac,
): IRbac {
  const result: IRbac = {};
  for (const [module, submodules] of Object.entries(structure)) {
    result[module] = {};
    for (const [submodule, defaultPerm] of Object.entries(submodules)) {
      const incoming = permissions?.[module]?.[submodule];
      result[module][submodule] = incoming
        ? { ...defaultPerm, ...incoming }
        : { ...defaultPerm };
    }
  }
  return result;
}
